import * as Analytics from 'analytics/analytics'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import BedbankMobileOnlyPriceLabel from 'components/Bedbank/BedbankMobileOnlyPriceLabel'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import LineSightseeingIcon from 'LuxKit/Icons/line/LineSightseeingIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { OfferPageActions, OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useToggle from 'hooks/useToggle'
import { take } from 'lib/array/arrayUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo } from 'react'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileActions, { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileDetails from 'components/AccommodationKit/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import BedbankRoomOptionRate from './BedbankRoomOptionRate'
import { hasSelectedRooms, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { RoomTypeAvailabilityStatus } from 'hooks/useBedbankPackageViews'
import { smartScrollIntoViewIfNeeded } from 'lib/window/scrollUtils'
import { ROOM_MODE_TOGGLE_SECTION_ID } from '../MultipleRoomsBooking/Mobile/OfferPageRoomModeToggleSection'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'
import useOfferPageBedbankRates from '../hooks/useOfferPageBedbankRates'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const ShowMoreContent = styled(BodyText)`
  padding: ${rem(12)};
`

const INITIAL_RATES_COUNT_TO_DISPLAY = 4

interface Props {
  checkInDate?: string
  checkOutDate?: string
  offer: App.BedbankOffer
  packageView: App.BedbankOfferPackageView
  fetchingRates?: boolean
  isFilterActive: boolean
  isHighlighted?: boolean
  roomListId: string
  onViewPackageOptions: () => void
  roomPosition: number
  hasSelectedRooms: boolean
  rooms: Array<OfferPageRoom>
  availabilityStatus: RoomTypeAvailabilityStatus
}

function BedbankRoomOption(props: Props) {
  const {
    offer,
    packageView,
    checkInDate,
    checkOutDate,
    fetchingRates,
    isFilterActive,
    isHighlighted = false,
    roomListId,
    onViewPackageOptions,
    roomPosition,
    hasSelectedRooms,
    rooms,
    availabilityStatus,
  } = props

  const { value: showingMoreRates, toggle: showMoreRates } = useToggle(false)

  const { rates, isFlexCfmrBedbankPackagesEnabled } = useOfferPageBedbankRates(packageView.effectiveRates, checkInDate, checkOutDate)

  const promotions = packageView.promotions
  const hasMobileOnlyPrice = useMemo(() => {
    return rates.some(rate => rate.mobilePromotion)
  }, [rates])

  const isInitialPackageOption = !!(promotions[rates[0]?.promotionId ?? ''])

  const canBuyNow = packageView.available && packageView.capacityValid && packageView.show && !fetchingRates

  // use full rates as dep so when user expand/hide rates the list id stay the same
  const rateListId = useMemo(() => uuidV4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rates, canBuyNow])

  const hasPromotions = offer.promotions?.length > 0

  useEffect(() => {
    if (isHighlighted) {
      onViewPackageOptions()
    }
  }, [isHighlighted]) // eslint-disable-line react-hooks/exhaustive-deps

  const ratesToDisplay = useMemo(() => {
    if (canBuyNow) {
      if (showingMoreRates) {
        return rates
      } else {
        return take(rates, INITIAL_RATES_COUNT_TO_DISPLAY)
      }
    }
    return []
  }, [canBuyNow, showingMoreRates, rates])

  const validCheckinCheckout = !!(checkInDate && checkOutDate)
  const hotelAvailable = packageView.available
  const canPurchase = rates.length > 0 && packageView.capacityValid && hotelAvailable && !fetchingRates

  const roomTileActionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (fetchingRates) {
      return 'loading'
    }
    if (!canPurchase) {
      if (availabilityStatus === 'partiallyAvailable') {
        return 'available-for-some-rooms'
      }
      if (validCheckinCheckout && packageView.capacityValid) {
        return 'sold-out-on-dates'
      }
      if (hotelAvailable && !packageView.capacityValid) {
        return 'exceeded-capacity'
      }
    }
    if (validCheckinCheckout && !hotelAvailable && packageView.capacityValid) {
      return 'sold-out'
    }
    if (!isFilterActive && rates.length == 0 && checkInDate && checkOutDate) {
      return 'sold-out'
    }
    if (hotelAvailable && !packageView.capacityValid) {
      return 'exceeded-capacity'
    }
    if (hotelAvailable && packageView.capacityValid && !validCheckinCheckout) {
      return 'available-on-dates'
    }
  }, [canPurchase, checkInDate, checkOutDate, fetchingRates, hotelAvailable, isFilterActive, packageView.capacityValid, rates.length, validCheckinCheckout, availabilityStatus])

  const offerPageDispatch = useOfferPageDispatch()

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, packageView.package.roomId])

  const handleSelectDates = useCallback(() => {
    toggleFilter('checkin')
  }, [toggleFilter])

  const onChangeOccupancyClick = useCallback(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  const handleCheckAvailabilityClick = useCallback(() => {
    toggleFilter(hasSelectedRooms ? 'checkin' : 'capacity')
    Analytics.trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: packageView.package.roomId }))
  }, [offer, packageView.package.roomId, hasSelectedRooms, toggleFilter])

  const onMultiRoomModeClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleMultiRoomMode, multiRoomMode: 'individual' })
    const toggleElement = document.getElementById(ROOM_MODE_TOGGLE_SECTION_ID)
    if (toggleElement) {
      smartScrollIntoViewIfNeeded(toggleElement)
    }
  }, [offerPageDispatch])

  const isMysteryRoom = packageView.package.name.toLowerCase().includes('mystery room offer')

  return <Pane border="neutral-five" borderRadius="S" data-testid="PackageOption-bedbank">
    {isHighlighted && <MessageBanner kind="promotional-primary" icon={<LineSightseeingIcon />}>
      <b>You were interested in this option</b>
    </MessageBanner>}
    <ScrollAnchor id={packageView.package.roomId} vOffset={rem(80)} />
    <AccommodationRoomTile roomPackage={packageView.package}>
      <AccommodationRoomTileImage roomPackage={packageView.package} />
      <AccommodationRoomTileDetails
        roomPackage={packageView.package}
        roomCount={rooms.length}
        urgencyLabels={<>
          {isMysteryRoom && <PromotionLabel variant="default" icon={<LineAwardIcon />}>Lowest price</PromotionLabel>}
          {hasPromotions && <BookingsCountLabel
            bookings={packageView.package.stats.booked}
            threshold="room"
          />}
          {hasMobileOnlyPrice && <BedbankMobileOnlyPriceLabel />}
        </>}
        actions={<TripGuard>
          <BookmarkButton offer={offer} roomTypeId={packageView.package.roomId} withLabel="tablet" />
        </TripGuard>}
      >
        <AccommodationRoomTileActions
          roomPackage={packageView.package}
          state={roomTileActionsState}
          onChangeOccupancyClick={onChangeOccupancyClick}
          onChangeDatesClick={handleSelectDates}
          onCheckAvailabilityClick={handleCheckAvailabilityClick}
          onMultiRoomModeClick={onMultiRoomModeClick}
        />
      </AccommodationRoomTileDetails>
    </AccommodationRoomTile>
    <RateSection data-testid="rate-options">
      {ratesToDisplay.map((rate, i) => <BedbankRoomOptionRate
        key={rate.id}
        rate={rate}
        offer={offer}
        roomListId={roomListId}
        packageView={packageView}
        optionNumber={i + 1}
        isInitialPackageOption={isInitialPackageOption}
        rateListId={rateListId}
        roomPosition={roomPosition}
        isFlexCfmrBedbankPackagesEnabled={isFlexCfmrBedbankPackagesEnabled}
      />)}
      {rates.length > INITIAL_RATES_COUNT_TO_DISPLAY && <ShowMoreContent variant="medium" align="center">
        <SmallToggleButton
          on={showingMoreRates}
          onClick={showMoreRates}
        >
          {showingMoreRates ? 'Show less options' : 'Show all options'}
        </SmallToggleButton>
      </ShowMoreContent>}
    </RateSection>
  </Pane>
}

type MappedProps = Pick<Props,
  | 'isFilterActive'
  | 'checkInDate'
  | 'checkOutDate'
  | 'hasSelectedRooms'
  | 'rooms'
> & { open: boolean }

export default withOfferPageAndReduxState((offerState): MappedProps => ({
  isFilterActive: !!offerState.activeFilter,
  checkInDate: offerState.checkInDate,
  checkOutDate: getCheckoutDate(offerState),
  open: !!offerState.activeFilter,
  hasSelectedRooms: hasSelectedRooms(offerState.rooms),
  rooms: offerState.rooms,
}), BedbankRoomOption, preventRenderWithOpenFilter)
